import React, { useRef } from 'react';
import { FiX, FiDownload } from 'react-icons/fi';
import { useAuthStore } from '../../stores';

interface SharePositionsModalProps {
  account: any;
  position: any;
  onClose: () => void;
}

const SharePositionsModal: React.FC<SharePositionsModalProps> = ({ account, position, onClose }) => {
  const { user } = useAuthStore();
  const shareCardRef = useRef<HTMLDivElement>(null);

  const formatCurrency = (value: number | string) => {
    return `$${parseFloat(value?.toString() || '0').toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (value: number | string, isDecimal: boolean = false) => {
    const num = parseFloat(value?.toString() || '0');
    const percent = isDecimal ? num * 100 : num;
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(2)}%`;
  };

  const handleDownload = async () => {
    if (!shareCardRef.current) return;

    try {
      // Check if html2canvas is loaded
      // @ts-ignore
      if (typeof window.html2canvas === 'undefined') {
        alert('Image generator is still loading. Please wait a moment and try again.');
        return;
      }

      // @ts-ignore - html2canvas is loaded from CDN
      const canvas = await window.html2canvas(shareCardRef.current, {
        backgroundColor: '#0f172a',
        scale: 2,
        logging: true,
        useCORS: true,
        allowTaint: true,
      });

      // Convert canvas to blob for better compatibility
      canvas.toBlob((blob: Blob | null) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `trade-pilot-${symbol}-${new Date().getTime()}.png`;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Error generating image:', error);
      alert(`Failed to generate image: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
    }
  };

  // Calculate position P/L - handle both snake_case and camelCase formats
  // Also handle normalized data from AccountDetails
  const qty = parseFloat(position.qty || position.quantity || '0');
  const avgEntry = parseFloat(position.avg_entry_price || position.avgEntryPrice || '0');
  const currentPrice = parseFloat(position.current_price || position.currentPrice || position.last_price || position.lastPrice || position.price || '0');
  const marketValue = parseFloat(position.market_value || position.marketValue || (qty * currentPrice) || '0');
  const costBasis = parseFloat(position.cost_basis || position.costBasis || (qty * avgEntry) || '0');
  const unrealizedPL = parseFloat(position.unrealized_pl || position.unrealizedPl || (marketValue - costBasis) || '0');
  
  // Handle P/L percent - can be in decimal (0.05 = 5%) or percentage (5 = 5%) format
  let unrealizedPLPercent = 0;
  if (position.unrealized_plpc !== undefined && position.unrealized_plpc !== null) {
    unrealizedPLPercent = parseFloat(position.unrealized_plpc) * 100; // Convert decimal to percent
  } else if (position.unrealizedPlPercent !== undefined && position.unrealizedPlPercent !== null) {
    unrealizedPLPercent = parseFloat(position.unrealizedPlPercent); // Already in percent
  } else if (costBasis > 0) {
    unrealizedPLPercent = (unrealizedPL / costBasis) * 100; // Calculate from P/L and cost basis
  }
  
  // Today's P/L - handle both formats
  const todayPL = parseFloat(position.today_unrealized_pl || position.todayUnrealizedPl || position.unrealized_intraday_pl || position.unrealizedIntradayPl || '0');
  let todayPLPercent = 0;
  if (position.today_unrealized_plpc !== undefined && position.today_unrealized_plpc !== null) {
    todayPLPercent = parseFloat(position.today_unrealized_plpc) * 100;
  } else if (position.todayUnrealizedPlPercent !== undefined && position.todayUnrealizedPlPercent !== null) {
    todayPLPercent = parseFloat(position.todayUnrealizedPlPercent);
  } else if (position.unrealized_intraday_plpc !== undefined && position.unrealized_intraday_plpc !== null) {
    todayPLPercent = parseFloat(position.unrealized_intraday_plpc) * 100;
  }
  
  // Extract symbol - handle both string and object formats
  let symbol = '';
  if (typeof position.symbol === 'string') {
    symbol = position.symbol.trim();
  } else if (position.symbol && typeof position.symbol === 'object') {
    symbol = position.symbol.symbol || position.symbol.ticker || position.symbol.name || position.symbol.raw_symbol || '';
    symbol = String(symbol || '').trim();
  } else {
    symbol = String(position.symbol || 'UNKNOWN').trim();
  }
  
  // Extract side and asset class
  const side = position.side || (qty >= 0 ? 'long' : 'short');
  const assetClass = position.asset_class || position.assetClass || 'us_equity';

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h3 className="text-2xl font-bold text-foreground">Share Position - {symbol}</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-elevated rounded-lg transition-colors"
          >
            <FiX className="h-6 w-6 text-muted" />
          </button>
        </div>

        {/* Share Card - This will be captured */}
        <div className="p-6 flex justify-center">
          <div
            ref={shareCardRef}
            style={{
              width: '600px',
              background: '#5B9BD5',
              padding: '48px 40px',
              borderRadius: '32px',
              fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            {/* Header */}
            <div style={{ marginBottom: '24px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#ffffff', marginBottom: '8px' }}>
                Trade Pilot
              </div>
              <div style={{ fontSize: '20px', fontWeight: '600', color: 'rgba(255,255,255,0.95)', marginBottom: '4px' }}>
                {symbol} {formatCurrency(currentPrice)}
              </div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                {assetClass === 'us_equity' ? 'US Equity' : assetClass?.toUpperCase() || 'Stock'} • {side === 'long' ? 'Long' : 'Short'} Position
              </div>
            </div>

            {/* Decorative Line */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.2)' }}></div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Total P/L
              </div>
              <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.2)' }}></div>
            </div>

            {/* Main P/L Display */}
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{ fontSize: '72px', fontWeight: '700', color: '#ffffff', lineHeight: '1', marginBottom: '8px', textShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                {unrealizedPL >= 0 ? '+' : ''}{formatCurrency(unrealizedPL)}
              </div>
              <div style={{ fontSize: '32px', fontWeight: '600', color: 'rgba(255,255,255,0.95)' }}>
                {formatPercent(unrealizedPLPercent)}
              </div>
            </div>

            {/* Visual Element - Trade Pilot Logo */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '32px', minHeight: '200px' }}>
              <img 
                src="/card-image.png" 
                alt="Trade Pilot" 
                style={{ 
                  width: '400px', 
                  height: 'auto', 
                  opacity: 0.95,
                  filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.8)) drop-shadow(0 0 40px rgba(255,255,255,0.5))',
                }}
              />
            </div>

            {/* Position Stats - Compact Grid */}
            <div style={{ 
              backgroundColor: 'rgba(255,255,255,0.15)', 
              borderRadius: '20px', 
              padding: '24px',
              backdropFilter: 'blur(10px)',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Quantity
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff' }}>
                    {qty.toFixed(2)} shares
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Avg Entry
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff' }}>
                    {formatCurrency(avgEntry)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Market Value
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff' }}>
                    {formatCurrency(marketValue)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Today's P/L
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff' }}>
                    {todayPL === 0 ? '-' : formatCurrency(todayPL)}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.9)', fontWeight: '600', marginBottom: '2px' }}>
                  {user?.name || 'Trader'}
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>
                  {new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })} {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '14px', color: '#ffffff', fontWeight: '700', marginBottom: '2px' }}>
                  Trade Pilot
                </div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)' }}>
                  tradepilot.com
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4 p-6 border-t border-border">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            className="btn-primary flex items-center gap-2"
          >
            <FiDownload className="h-5 w-5" />
            <span>Download as Image</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SharePositionsModal;

